import { Disclosure } from "@headlessui/react";
import classNames from "classnames";
import { LoadingIcon } from "components/LoadingIcon/LoadingIcon";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import React, { Fragment, ReactElement, useEffect, useState } from "react";

// Base types for accordion items
type BaseAccordionItemProps = {
  title: string | React.ReactNode;
  subtitle?: React.ReactNode;
  persistentSubtitle?: boolean;
  content: React.ReactNode;
  itemKey: string;
};

// Internal props that get added by the Accordion component
type InternalAccordionItemProps = BaseAccordionItemProps & {
  isOpen?: boolean;
  onClick: (key: string) => void;
};

// Props for when used as children (onClick and isOpen are optional since they're injected)
type AccordionItemChildProps = BaseAccordionItemProps & {
  isOpen?: boolean;
  onClick?: (key: string) => void;
};

// Props specifically for LoadingStepAccordionItem
type LoadingStepAccordionItemProps = AccordionItemChildProps & {
  status?: "IN_PROGRESS" | "COMPLETE" | "PENDING";
  isLast?: boolean;
  hideChevron?: boolean;
};

type AccordionProps = {
  children: ReactElement<BaseAccordionItemProps>[];
  defaultOpen?: string | null;
  forceOpenItem?: string | null;
};

export function Accordion({
  children,
  defaultOpen = null,
  forceOpenItem = null,
}: AccordionProps) {
  const [openItem, setOpenItem] = useState<string | null>(
    defaultOpen ?? forceOpenItem ?? null
  );

  useEffect(() => {
    if (forceOpenItem) {
      setOpenItem(forceOpenItem);
    }
  }, [forceOpenItem]);

  const handleClick = (key: string) => {
    if (openItem === key) {
      setOpenItem(null);
    } else {
      setOpenItem(key);
    }
  };

  return (
    <div className="overflow-hidden">
      {children.map((child) => {
        return React.cloneElement(child, {
          ...child.props,
          isOpen: openItem === child.props.itemKey,
          onClick: handleClick,
        } as InternalAccordionItemProps);
      })}
    </div>
  );
}

// Compound Components
const AccordionButton = ({
  title,
  isOpen,
  onClick,
  itemKey,
  leftContent,
  titleClassName = "base-bold",
  iconClassName = "text-fg-base",
  hideChevron = false,
}: {
  title: string | React.ReactNode;
  isOpen?: boolean;
  onClick?: (key: string) => void;
  itemKey: string;
  leftContent?: React.ReactNode;
  titleClassName?: string;
  iconClassName?: string;
  hideChevron?: boolean;
}) => {
  const handleOpen = () => {
    onClick?.(itemKey);
  };

  return (
    <Disclosure.Button
      onClick={handleOpen}
      onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === "Enter" || e.key === " ") {
          handleOpen();
        }
      }}
      className="w-full"
    >
      <div className="flex items-center justify-between w-full h-10">
        <div className="flex items-center gap-4">
          {leftContent}
          {typeof title === "string" || typeof title === "number" ? (
            <p className={titleClassName}>{title}</p>
          ) : (
            title
          )}
        </div>
        {!hideChevron &&
          (isOpen ? (
            <ChevronUp size={20} className={iconClassName} />
          ) : (
            <ChevronDown size={20} className={iconClassName} />
          ))}
      </div>
    </Disclosure.Button>
  );
};

const AccordionContent = ({
  isOpen,
  content,
}: {
  isOpen?: boolean;
  content: React.ReactNode;
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <Disclosure.Panel static as={Fragment}>
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{
              height: "auto",
              opacity: 1,
              transition: {
                opacity: { delay: 0.1, duration: 0.2, ease: "easeIn" },
                height: { duration: 0.2, ease: "easeIn" },
              },
            }}
            exit={{
              height: 0,
              opacity: 0,
              transition: {
                opacity: { duration: 0.1, ease: "easeIn" },
                height: { delay: 0.05, duration: 0.2, ease: "easeIn" },
              },
            }}
          >
            <div className="mt-2">{content}</div>
          </motion.div>
        </Disclosure.Panel>
      )}
    </AnimatePresence>
  );
};

const AccordionSubtitle = ({
  isOpen,
  subtitle,
  persistentSubtitle = false,
}: {
  isOpen?: boolean;
  subtitle?: React.ReactNode;
  persistentSubtitle?: boolean;
}) => {
  if (!subtitle || (!persistentSubtitle && isOpen)) return null;

  return (
    <AnimatePresence>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="sm-normal text-fg-muted text-left"
      >
        {subtitle}
      </motion.p>
    </AnimatePresence>
  );
};

export const BorderedAccordionItem = ({
  title,
  subtitle,
  content,
  itemKey,
  isOpen,
  onClick,
  persistentSubtitle = false,
}: AccordionItemChildProps) => {
  return (
    <Disclosure>
      <div className="border-b border-border-base last:border-b-0 pt-2 pb-4 px-4">
        <AccordionButton
          title={title}
          isOpen={isOpen}
          onClick={onClick}
          itemKey={itemKey}
        />
        <AccordionContent isOpen={isOpen} content={content} />
        <AccordionSubtitle
          isOpen={isOpen}
          subtitle={subtitle}
          persistentSubtitle={persistentSubtitle}
        />
      </div>
    </Disclosure>
  );
};

const VerticalLine = ({
  status,
}: {
  status: "IN_PROGRESS" | "COMPLETE" | "PENDING";
}) => {
  return (
    <div
      className={classNames("w-[3px] rounded-full -mt-[3px] -mb-[15px]", {
        "bg-chart-green-base": status === "COMPLETE",
        "bg-border-base": status === "PENDING",
        "bg-linear-to-b from-chart-green-base to-chart-green-faint animate-pulse":
          status === "IN_PROGRESS",
      })}
      style={{
        height: "calc(100% + 18px)", // Value of 18px is derived from 3px top margin and 15px bottom margin
      }}
    />
  );
};

export const LoadingStepAccordionItem = ({
  title,
  subtitle,
  content,
  itemKey,
  isOpen,
  onClick,
  persistentSubtitle = false,
  status = "COMPLETE",
  isLast = false,
  hideChevron = false,
}: LoadingStepAccordionItemProps) => {
  const statusIcon =
    status === "IN_PROGRESS" ? (
      <LoadingIcon loading={true} size={20} className="text-chart-green-base" />
    ) : status === "COMPLETE" ? (
      <Check size={20} strokeWidth={3} className="text-chart-green-base" />
    ) : (
      <LoadingIcon size={20} loading={false} />
    );

  return (
    <div
      className={classNames(
        "px-2 rounded-xl hover:bg-bg-faint/50 transition-colors cursor-pointer",
        !persistentSubtitle && !isOpen ? "pb-0" : "pb-3"
      )}
      onClick={() => onClick?.(itemKey)}
    >
      <Disclosure>
        <AccordionButton
          title={title}
          isOpen={isOpen}
          itemKey={itemKey}
          leftContent={statusIcon}
          titleClassName="sm-md"
          iconClassName="text-fg-muted"
          hideChevron={hideChevron}
        />
        <div className="flex items-stretch gap-4">
          <div className="w-5 flex justify-center flex-none">
            {!isLast && <VerticalLine status={status} />}
          </div>
          <div className="flex flex-col gap-6">
            <AccordionContent isOpen={isOpen} content={content} />
            <AccordionSubtitle
              isOpen={isOpen}
              subtitle={subtitle}
              persistentSubtitle={persistentSubtitle}
            />
          </div>
        </div>
      </Disclosure>
    </div>
  );
};

// Legacy export for backward compatibility
export type AccordionItems = BaseAccordionItemProps[];
